/** Structural wire contract; clinical wording is owned by the worklist. */
export const OBSERVATION_KINDS = {
  lung_bases: "bilateral",
  cardiac_landmark: "binary",
  pulmonary_trunk_bifurcation: "binary",
  carina: "binary",
  lung_apices: "bilateral",
} as const;
export type ObservationId = keyof typeof OBSERVATION_KINDS;
export type ObservationValue =
  | "yes"
  | "no"
  | "both"
  | "right"
  | "left"
  | "neither"
  | "unassessable";
export interface ObservationDefinition {
  id: ObservationId;
  kind: "binary" | "bilateral";
  label: string;
  instructions: string;
}
export interface StudyObservationTask {
  contract: "thoracic_anatomy";
  revision: 1;
  schema_revision: string;
  instruction_revision: string;
  title: string;
  instructions: string;
  cardiac_landmark: "superior_cavoatrial_junction" | "left_atrial_roof";
  observations: ObservationDefinition[];
}
export interface ObservationAnswer {
  value: ObservationValue;
  series_evidence?: { present?: string; right?: string; left?: string };
}
export type ObservationAnswers = Partial<Record<ObservationId, ObservationAnswer>>;
export interface StudyObservationInput {
  task_hash: string;
  schema_revision: string;
  instruction_revision: string;
  expected_record_revision: string | null;
  observations: ObservationAnswers;
  chest_presence?: "yes" | "no" | "unassessable";
  chest_presence_series_uid?: string;
  note?: string;
}
export interface StudyObservationRecord {
  project_id: string;
  study_instance_uid: string;
  case_id: string;
  task_hash: string;
  schema_revision: string;
  instruction_revision: string;
  record_revision: string;
  observations: ObservationAnswers;
  chest_presence: "yes" | "no" | "unassessable";
  chest_presence_basis: "landmark" | "direct";
  chest_presence_series_uid?: string;
  reader: string;
  recorded_at: string;
  note?: string;
}
export interface StudyObservationResponse {
  project_id: string;
  study_instance_uid: string;
  case_id: string;
  task: StudyObservationTask;
  task_hash: string;
  eligible_series_uids: string[];
  record: StudyObservationRecord | null;
}
const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const text = (v: unknown): v is string => typeof v === "string" && v.trim().length > 0;
export const isPositive = (value?: ObservationValue) =>
  ["yes", "both", "right", "left"].includes(value ?? "");
export const evidenceSides = (value: ObservationValue): Array<"present" | "right" | "left"> =>
  value === "yes"
    ? ["present"]
    : value === "both"
      ? ["right", "left"]
      : value === "right" || value === "left"
        ? [value]
        : [];
export const hasLandmark = (answers: ObservationAnswers) =>
  Object.values(answers).some(
    (a) =>
      isPositive(a?.value) ||
      (a?.value === "unassessable" && !!(a.series_evidence?.right || a.series_evidence?.left)),
  );
export const needsChestAnswer = (answers: ObservationAnswers) =>
  Object.keys(OBSERVATION_KINDS).every((id) => answers[id as ObservationId]) &&
  !hasLandmark(answers);

export function validateTask(value: unknown): asserts value is StudyObservationTask {
  if (
    !isObject(value) ||
    value.contract !== "thoracic_anatomy" ||
    value.revision !== 1 ||
    !["schema_revision", "instruction_revision", "title", "instructions"].every((k) =>
      text(value[k]),
    ) ||
    !["superior_cavoatrial_junction", "left_atrial_roof"].includes(
      String(value.cardiac_landmark),
    ) ||
    !Array.isArray(value.observations) ||
    value.observations.length !== 5
  )
    throw new Error(
      "Unsupported anatomy task configuration. Reload the worklist or contact the study team.",
    );
  const ids = new Set<string>();
  for (const row of value.observations) {
    if (
      !isObject(row) ||
      !text(row.id) ||
      !(row.id in OBSERVATION_KINDS) ||
      ids.has(row.id) ||
      row.kind !== OBSERVATION_KINDS[row.id as ObservationId] ||
      !text(row.label) ||
      !text(row.instructions)
    )
      throw new Error("Invalid anatomy observations. Contact the study team.");
    ids.add(row.id);
  }
}

export function validateAnswers(
  answers: unknown,
  eligible: string[],
): answers is ObservationAnswers {
  if (!isObject(answers) || Object.keys(answers).length !== 5) return false;
  return Object.entries(OBSERVATION_KINDS).every(([id, kind]) => {
    const answer = answers[id];
    const options =
      kind === "binary"
        ? ["yes", "no", "unassessable"]
        : ["both", "right", "left", "neither", "unassessable"];
    if (!isObject(answer) || !options.includes(String(answer.value))) return false;
    const evidence = answer.series_evidence ?? {};
    if (!isObject(evidence)) return false;
    const sides =
      answer.value === "unassessable" && kind === "bilateral"
        ? Object.keys(evidence)
        : evidenceSides(answer.value as ObservationValue);
    if (
      answer.value === "unassessable" &&
      kind === "bilateral" &&
      (sides.length > 1 || sides.some((side) => !["right", "left"].includes(side)))
    )
      return false;
    return (
      isObject(evidence) &&
      Object.keys(evidence).length === sides.length &&
      sides.every((side) => eligible.includes(String(evidence[side])))
    );
  });
}

export function validateResponse(
  value: unknown,
  projectId: string,
  studyUid: string,
  caseId: string,
): asserts value is StudyObservationResponse {
  if (
    !isObject(value) ||
    value.project_id !== projectId ||
    value.study_instance_uid !== studyUid ||
    value.case_id !== caseId ||
    !text(value.task_hash) ||
    !Array.isArray(value.eligible_series_uids) ||
    !value.eligible_series_uids.every((uid) => text(uid) && /^[0-9.]+$/.test(uid))
  )
    throw new Error("The anatomy task does not match this study. Reload the worklist.");
  validateTask(value.task);
  if (value.record === null) return;
  const r = value.record;
  if (
    !isObject(r) ||
    r.project_id !== projectId ||
    r.study_instance_uid !== studyUid ||
    r.case_id !== caseId ||
    r.task_hash !== value.task_hash ||
    r.schema_revision !== value.task.schema_revision ||
    r.instruction_revision !== value.task.instruction_revision ||
    !text(r.record_revision) ||
    !text(r.reader) ||
    !text(r.recorded_at) ||
    !validateAnswers(r.observations, value.eligible_series_uids as string[]) ||
    !["yes", "no", "unassessable"].includes(String(r.chest_presence)) ||
    r.chest_presence_basis !== (hasLandmark(r.observations) ? "landmark" : "direct") ||
    (hasLandmark(r.observations) && r.chest_presence !== "yes") ||
    (r.chest_presence_basis === "direct" && r.chest_presence === "yes"
      ? !(value.eligible_series_uids as string[]).includes(String(r.chest_presence_series_uid))
      : r.chest_presence_series_uid != null)
  )
    throw new Error(
      "Saved anatomy answers are incompatible with this task. Contact the study team before editing.",
    );
}
